import React, { useState } from "react";
import {
  ButtonGroup,
  ButtonGroupProps,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { Stage } from "../../lusca-search-api/generated-src";
import { fetchAgentPrompt } from "../../hooks/useInvestigations";

export const COPY_AGENT_PROMPT_ID = "copy-agent-prompt";
export const REFRESH_ID = "refresh";

interface InvestigationAgentPromptProps {
  stage: Stage;
  investigationId: string;
  /** When provided, a Refresh icon button is rendered in the same group. */
  onRefresh?: () => void;
}

type CopyState = "idle" | "copying" | "copied" | "failed";

const FEEDBACK: Record<Exclude<CopyState, "idle">, React.ReactNode> = {
  copying: <StatusIndicator type="loading">Copying…</StatusIndicator>,
  copied: <StatusIndicator type="success">Agent prompt copied</StatusIndicator>,
  failed: <StatusIndicator type="error">Copy failed</StatusIndicator>,
};

/**
 * Icon-only "Copy agent prompt" action (tooltip on hover, feedback popover on click). Copies a
 * prompt that lets a local agent continue the case through the Lusca MCP server under the user's
 * own identity (every agent mutation is audited as source=mcp).
 */
export const InvestigationAgentPrompt: React.FC<
  InvestigationAgentPromptProps
> = ({ stage, investigationId, onRefresh }) => {
  const [copyState, setCopyState] = useState<CopyState>("idle");

  const copy = async () => {
    setCopyState("copying");
    try {
      const content = await fetchAgentPrompt(stage, investigationId);
      await navigator.clipboard.writeText(content);
      setCopyState("copied");
    } catch (err) {
      console.error("Failed to copy agent prompt:", err);
      setCopyState("failed");
    }
  };

  const items: ButtonGroupProps.Item[] = [
    {
      type: "icon-button",
      id: COPY_AGENT_PROMPT_ID,
      iconName: "copy",
      text: "Copy agent prompt",
      // Always present so the feedback popover opens on click; content follows copyState.
      popoverFeedback: FEEDBACK[copyState === "idle" ? "copying" : copyState],
    },
  ];
  if (onRefresh) {
    items.push({
      type: "icon-button",
      id: REFRESH_ID,
      iconName: "refresh",
      text: "Refresh",
    });
  }

  return (
    <ButtonGroup
      variant="icon"
      ariaLabel="Investigation actions"
      items={items}
      onItemClick={({ detail }) => {
        if (detail.id === COPY_AGENT_PROMPT_ID) {
          copy().catch(console.error);
        } else if (detail.id === REFRESH_ID) {
          onRefresh?.();
        }
      }}
    />
  );
};

export default InvestigationAgentPrompt;
